import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, Textarea, useToast } from "@/design-system/components/ui";
import { calendarDateInputToIso } from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { extractErrorMessage } from "@/api/client";
import { buyerActionsApi, type LogBuyerContactRequest } from "@/api/buyerActions";
import type { CommunicationChannel, CommunicationDirection } from "@/api/communications";
import {
  CALL_OUTCOMES,
  CHANNEL_LABELS,
  COMMUNICATION_CHANNELS,
  COMMUNICATION_DIRECTIONS,
  DEFAULT_MEETING_MODALITY,
  MEETING_MODALITIES,
  isRequired,
  quickFormShows,
  validateChannelFields,
  type ChannelRule,
} from "@/lib/communicationChannelRules";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { communicationKeys } from "@/pages/communications/hooks/useCommunications";
import { activityKeys } from "@/pages/activity/hooks/useActivityByOwner";

export interface LogContactModalProps {
  buyerLeadId: string;
  buyerName: string;
  onClose: () => void;
  onLogged?: () => void;
}

/**
 * Log a contact with a buyer, from the detail strip or the list row menu.
 *
 * 🔴 It asks for the channel-specific fields the server requires, from the
 * SAME definition the full "Log communication" form reads. It did not, and
 * three of five channels were unloggable because of it: Email and Meeting
 * always (the request could not carry a Subject or a Modality at all), and
 * Call whenever the outcome was left blank — which is the default state of
 * the default channel. Each failed as a generic 500, because the rule's own
 * message was thrown as a type the middleware has no arm for.
 *
 * WHAT THIS FORM SHOWS is `quickFormShows`: what the channel REQUIRES, plus
 * Outcome wherever one is offered. It is deliberately not the full form —
 * duration, occurred-at, addresses, participants and placements stay there.
 * The rule is keyed on the channel, never on the mount point.
 */
export function LogContactModal({ buyerLeadId, buyerName, onClose, onLogged }: LogContactModalProps) {
  const qc = useQueryClient();
  const toast = useToast();

  const [channel, setChannel] = useState<CommunicationChannel>("Call");
  const [direction, setDirection] = useState<CommunicationDirection>("Outbound");
  const [subject, setSubject] = useState("");
  const [outcome, setOutcome] = useState("");
  const [modality, setModality] = useState<string>(DEFAULT_MEETING_MODALITY);
  const [note, setNote] = useState("");
  const [withFollowUp, setWithFollowUp] = useState(false);
  const [followUpTitle, setFollowUpTitle] = useState(`Follow up with ${buyerName}`);
  const [followUpDate, setFollowUpDate] = useState("");
  const [errors, setErrors] = useState<Partial<Record<keyof ChannelRule, string>>>({});

  const shows = (field: keyof ChannelRule) => quickFormShows(channel, field);

  const noteOk = note.trim().length > 0;
  const followUpDueIso = withFollowUp ? calendarDateInputToIso(followUpDate) : null;
  const followUpOk = !withFollowUp || (followUpTitle.trim().length > 0 && followUpDueIso != null);
  const canSubmit = noteOk && followUpOk;

  /**
   * The channel decides which questions are on screen, so changing it clears
   * the answers to questions that are no longer being asked — and the errors
   * raised against them, which would otherwise outlive their own field.
   */
  function selectChannel(next: CommunicationChannel) {
    if (next === channel) return;
    setChannel(next);
    setOutcome("");
    setSubject("");
    setModality(DEFAULT_MEETING_MODALITY);
    setErrors({});
  }

  /**
   * Only what this form asked for. A field the channel hides is sent as null,
   * exactly as the full form sends it — a meeting carries no direction
   * because a meeting is not directional.
   */
  function buildBody(): LogBuyerContactRequest {
    return {
      channel,
      direction: shows("direction") ? direction : null,
      subject: shows("subject") ? subject.trim() || null : null,
      outcome: shows("outcome") ? outcome || null : null,
      modality: shows("modality") ? modality || null : null,
      note: note.trim(),
      followUp:
        withFollowUp && followUpDueIso
          ? { title: followUpTitle.trim(), dueDate: followUpDueIso }
          : null,
    };
  }

  const mutation = useMutation({
    mutationFn: () => buyerActionsApi.logContact(buyerLeadId, buildBody()),
    onSuccess: (result) => {
      // The list row (last contact, next action, the needs-action badges), the
      // stat cards and the buyer's detail all read the stamps this moved.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      // The server also wrote a `CommunicationLogEntry` placed on this buyer,
      // so the Communications tab (its own key family, NOT under `buyerKeys`)
      // and the Activity feed must learn about it now, not after staleTime.
      qc.invalidateQueries({ queryKey: communicationKeys.all });
      qc.invalidateQueries({ queryKey: activityKeys.byOwner("buyer_lead", buyerLeadId) });
      toast.success(
        result.followUpTaskId ? "Contact logged and follow-up scheduled." : "Contact logged.",
      );
      onLogged?.();
      onClose();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  /**
   * Caught here rather than at the server, as the full form does — the old
   * behaviour let this reach the server and fail there, and the failure was
   * not even legible when it arrived.
   */
  function submit() {
    if (!canSubmit || mutation.isPending) return;

    const next = validateChannelFields(
      channel,
      { direction, subject, outcome, modality },
      quickFormShows,
    );
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={`Log contact — ${buyerName}`} size="md">
      <form
        className="grid gap-space-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        data-testid="log-contact-form"
      >
        <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
          <FormField label="Channel" required htmlFor="log-contact-channel">
            <Select
              id="log-contact-channel"
              value={channel}
              onChange={(e) => selectChannel(e.target.value as CommunicationChannel)}
              data-testid="log-contact-channel"
            >
              {COMMUNICATION_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABELS[c]}
                </option>
              ))}
            </Select>
          </FormField>

          {shows("direction") && (
            <FormField
              label="Direction"
              required={isRequired(channel, "direction")}
              htmlFor="log-contact-direction"
              error={errors.direction}
            >
              <Select
                id="log-contact-direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value as CommunicationDirection)}
                data-testid="log-contact-direction"
              >
                {COMMUNICATION_DIRECTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </div>

        {shows("subject") && (
          <FormField
            label="Subject"
            required={isRequired(channel, "subject")}
            htmlFor="log-contact-subject"
            error={errors.subject}
          >
            <Input
              id="log-contact-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              invalid={Boolean(errors.subject)}
              data-testid="log-contact-subject"
            />
          </FormField>
        )}

        {shows("outcome") && (
          <FormField
            label="Outcome"
            required={isRequired(channel, "outcome")}
            htmlFor="log-contact-outcome"
            error={errors.outcome}
          >
            <Select
              id="log-contact-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              data-testid="log-contact-outcome"
            >
              <option value="">— Select outcome —</option>
              {CALL_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        {shows("modality") && (
          <FormField
            label="Modality"
            required={isRequired(channel, "modality")}
            htmlFor="log-contact-modality"
            error={errors.modality}
          >
            <Select
              id="log-contact-modality"
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              data-testid="log-contact-modality"
            >
              {MEETING_MODALITIES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField label="Note" required htmlFor="log-contact-note">
          <Textarea
            id="log-contact-note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was said, and what was agreed."
            data-testid="log-contact-note"
          />
        </FormField>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={withFollowUp}
            onChange={(e) => setWithFollowUp(e.target.checked)}
            data-testid="log-contact-follow-up-toggle"
          />
          Schedule a follow-up
        </label>

        {withFollowUp && (
          <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
            <FormField label="Follow-up title" required>
              <Input
                value={followUpTitle}
                onChange={(e) => setFollowUpTitle(e.target.value)}
                data-testid="log-contact-follow-up-title"
              />
            </FormField>
            <FormField label="Due" required>
              <Input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                data-testid="log-contact-follow-up-date"
              />
            </FormField>
          </div>
        )}

        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={!canSubmit || mutation.isPending}
            data-testid="log-contact-submit"
          >
            {mutation.isPending ? "Saving…" : "Log contact"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
